import React from 'react'

import { LoadingPlaceholder, WorkspaceLogo } from '@sketch/components'
import {
  AnnotationCommentsList,
  useCommentThreadSubscriptions,
} from '@sketch/modules-common'
import {
  CommentNotificationFragment,
  MentionNotificationFragment,
} from '@sketch/gql-types'

import {
  AnnotationCard,
  Breadcrumb,
  BreadcrumbDocumentIcon,
  BreadcrumbItem,
  BreadcrumbLabel,
  BreadcrumbSeparator,
  CanvasImage,
  CanvasImageWrapper,
  Card,
} from './CommentMentionPreview.styles'

interface CommentMentionPreviewProps {
  notification: CommentNotificationFragment | MentionNotificationFragment
}

export const CommentMentionPreview = ({
  notification,
}: CommentMentionPreviewProps) => {
  const { share, comment, user, previews } = notification
  const annotationIdentifier = notification.comment?.annotation.identifier

  // Keep the thread live while the preview is open — new/deleted/edited/read
  // replies and resolve/unresolve all update in real time. The inbox isn't
  // covered by the document-level `AnnotationSubscriptions`, so without this a
  // comment resolved elsewhere would leave a stale reply box here, and
  // replying would then fail with "Operation not allowed for resolved
  // annotations" (WEB-3726).
  useCommentThreadSubscriptions({
    shareIdentifier: share?.identifier ?? '',
    skip: !share?.identifier,
  })

  if (!share || !comment || !user || !annotationIdentifier) return null

  const previewUrl = previews[0]?.url ?? undefined
  const previewUrl2x = previews[1]?.url ?? undefined

  const previewSrcSet = previewUrl
    ? [`${previewUrl} 1x`, previewUrl2x && `${previewUrl2x} 2x`]
        .filter(Boolean)
        .join(', ')
    : undefined

  return (
    <Card>
      <Breadcrumb>
        <WorkspaceLogo
          size="24px"
          workspaceName={notification.workspace?.name}
          src={notification.workspace?.avatar?.small}
        />
        <BreadcrumbItem>
          <BreadcrumbLabel>{share.workspace.name}</BreadcrumbLabel>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
        </BreadcrumbItem>

        <BreadcrumbItem>
          <BreadcrumbDocumentIcon aria-hidden />
          <BreadcrumbLabel>{share.name}</BreadcrumbLabel>
        </BreadcrumbItem>
      </Breadcrumb>

      <CanvasImageWrapper>
        <CanvasImage
          src={previewUrl}
          srcSet={previewSrcSet}
          alt={previewUrl ? `Preview of ${share.name}` : ''}
          objectFit="cover"
          width="100%"
          height="100%"
          loadingPlaceholder={<LoadingPlaceholder size="64px" />}
        />
      </CanvasImageWrapper>

      <AnnotationCard>
        <AnnotationCommentsList
          annotationIdentifier={annotationIdentifier}
          isViewingLatestVersion
          commentType="FIXED"
        />
      </AnnotationCard>
    </Card>
  )
}
